import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../../../../../projects/my-app/src/environments/environment';
import { combineLatest, filter, Subscription } from 'rxjs';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { DashboardTitleService } from '../../shared-services/dashboard-title.service';
import { ThemeSelectorComponent } from '../theme-selector/theme-selector.component';
import { ThemeService } from '../../shared-services/theme.service';
import { LayoutService } from '../../shared-services/layout.service';
import { SlideshowService } from '../../shared-services/slideshow.service';

@Component({
  selector: 'app-side-navbar',
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatTooltipModule,
    RouterModule,
  ],
  templateUrl: './side-navbar.component.html',
  styleUrl: './side-navbar.component.scss',
})
export class SideNavbarComponent implements OnDestroy {
  /** Sidebar drag-resize bounds + persistence key. Min/max chosen so the
   *  menu icons + labels stay readable at min and don't crowd content at max. */
  private static readonly SIDEBAR_MIN_WIDTH = 200;
  private static readonly SIDEBAR_MAX_WIDTH = 480;
  private static readonly SIDEBAR_DEFAULT_WIDTH = 280;
  private static readonly SIDEBAR_WIDTH_STORAGE_KEY = 'dvm.sidebar.expandedWidth';

  isExpanded = true;
  showSubmenu: boolean = false;
  isShowing = false;
  showSubSubMenu: boolean = false;
  isCollapsed = true;
  isMobileLayout = false;
  isMobileDrawerOpen = false;
  username!: string;
  logo!: string;
  companyText = environment.COMPANY_LOGO_TEXT;
  secondaryLogo!: string;
  clientLogo: string = '';
  clientIcon: string = '';
  clientText: string = '';
  hasClientLogo: boolean = false;
  useAltSidenav: boolean = false;

  /** Width (px) used when the sidebar is expanded and not in mobile drawer
   *  mode. Hydrated from localStorage on init; updated live during drag. */
  sidebarExpandedWidth = SideNavbarComponent.SIDEBAR_DEFAULT_WIDTH;
  /** True while the user is actively dragging the resize handle. Suppresses
   *  the sidebar's width transition so width tracks the cursor 1:1. */
  isResizingSidebar = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private resizePointerId: number | null = null;

  // Light/Dark theme toggle
  private themeService = inject(ThemeService);
  private layoutService = inject(LayoutService);
  private slideshowService = inject(SlideshowService);
  private themeSub?: Subscription;
  private pendingPreviewSub?: Subscription;
  private slideshowStateSub?: Subscription;
  private slideshowProgressSub?: Subscription;
  private dashboardTitleSub?: Subscription;
  isDarkTheme = false;
  dashboardTitle = '';
  dashboardTitlePosition: 'top' | 'below' | 'default' = 'default';
  dashboardTitleFontSize = '';
  showThemeOptions = true;
  showThemeToggle = true;

  /** Drives the toolbar "Save Theme" button visibility — true after the user
   * hits Apply & Preview in the Theme Options dialog and not yet committed. */
  hasPendingThemePreview = false;

  /** Mirrored slideshow state — toolbar template binds to these for the
   *  inline Play/Pause/Apply/Save/Stop controls. Plain fields rather than
   *  async pipes so multiple template bindings on the same state field
   *  don't open separate subscriptions. */
  slideshowActive = false;
  slideshowPlaying = false;
  slideshowCurrentName = '';
  slideshowIndex = 0;
  slideshowTotal = 0;

  /** 0–1 progress through the current slide cycle. Bound to the progress
   *  bar's width %; updated each animation frame while playing. */
  slideshowProgress = 0;

  /** Live ref to the Theme Options dialog so re-clicking the toolbar button
   * while the dialog is already open is a no-op instead of stacking dialogs. */
  private themeSelectorDialogRef: MatDialogRef<ThemeSelectorComponent> | null = null;

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  /** Persist the previewed theme + customs + layout from the toolbar Save
   *  button. The single Save covers all three pending states so the user
   *  doesn't have to click multiple buttons after previewing changes. */
  saveThemePreview(): void {
    this.themeService.commitPendingPreview();
    this.layoutService.commitPendingLayout();
  }

  // ── Slideshow toolbar actions (delegate to SlideshowService) ─────

  /** Toggle the autoplay timer; the dashboard keeps showing the current
   *  slide either way. */
  toggleSlideshowPlayback(): void {
    if (this.slideshowPlaying) {
      this.slideshowService.pause();
    } else {
      this.slideshowService.play();
    }
  }

  slideshowNext(): void {
    this.slideshowService.next();
  }

  slideshowPrev(): void {
    this.slideshowService.prev();
  }

  /** "Apply & Preview" from the toolbar — keep the current slide as a
   *  pending preview (Save Theme button picks it up), end the slideshow. */
  slideshowApply(): void {
    this.slideshowService.applyAndStop();
  }

  /** Commit current slide to localStorage immediately + end. */
  slideshowSave(): void {
    this.slideshowService.saveAndStop();
  }

  /** Stop and revert to the theme that was active before Play was hit. */
  slideshowStop(): void {
    this.slideshowService.cancelAndStop();
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.pendingPreviewSub?.unsubscribe();
    this.slideshowStateSub?.unsubscribe();
    this.slideshowProgressSub?.unsubscribe();
    this.dashboardTitleSub?.unsubscribe();
    // If the user navigates away mid-drag the window listeners would leak
    // and keep firing on a destroyed component — clean them up here.
    if (this.isResizingSidebar) this.endSidebarResize(false);
  }

  /** Begin a drag-resize on the sidebar's right edge. No-op on mobile (the
   *  drawer is full-width) or when collapsed (handle isn't rendered). */
  startSidebarResize(event: PointerEvent): void {
    if (this.isMobileLayout || this.isCollapsed) return;
    event.preventDefault();
    event.stopPropagation();
    this.resizePointerId = event.pointerId;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.sidebarExpandedWidth;
    this.isResizingSidebar = true;
    window.addEventListener('pointermove', this.onResizePointerMove);
    window.addEventListener('pointerup', this.onResizePointerUp);
    window.addEventListener('pointercancel', this.onResizePointerUp);
  }

  private onResizePointerMove = (event: PointerEvent): void => {
    if (this.resizePointerId !== event.pointerId) return;
    const delta = event.clientX - this.resizeStartX;
    const next = this.clampSidebarWidth(this.resizeStartWidth + delta);
    if (next !== this.sidebarExpandedWidth) {
      this.sidebarExpandedWidth = next;
    }
  };

  private onResizePointerUp = (event: PointerEvent): void => {
    if (this.resizePointerId !== event.pointerId) return;
    this.endSidebarResize(true);
  };

  /** Tear down the drag — optionally persist the new width and trigger the
   *  same staged reflow used by collapse/expand so chart widgets resize. */
  protected endSidebarResize(commit: boolean): void {
    this.isResizingSidebar = false;
    this.resizePointerId = null;
    window.removeEventListener('pointermove', this.onResizePointerMove);
    window.removeEventListener('pointerup', this.onResizePointerUp);
    window.removeEventListener('pointercancel', this.onResizePointerUp);
    if (commit) {
      this.persistSidebarWidth();
      this.scheduleDesktopReflow();
    }
  }

  protected clampSidebarWidth(width: number): number {
    return Math.min(
      SideNavbarComponent.SIDEBAR_MAX_WIDTH,
      Math.max(SideNavbarComponent.SIDEBAR_MIN_WIDTH, Math.round(width)),
    );
  }

  protected persistSidebarWidth(): void {
    try {
      localStorage.setItem(
        SideNavbarComponent.SIDEBAR_WIDTH_STORAGE_KEY,
        String(this.sidebarExpandedWidth),
      );
    } catch {
      /* ignore */
    }
  }

  protected loadSidebarWidth(): void {
    try {
      const raw = localStorage.getItem(
        SideNavbarComponent.SIDEBAR_WIDTH_STORAGE_KEY,
      );
      if (!raw) return;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) return;
      this.sidebarExpandedWidth = this.clampSidebarWidth(parsed);
    } catch {
      /* ignore */
    }
  }

  /**
   * Select logos based on environment flags.
   * Use Case 1: CLIENT_LOGO_SRC empty → show default SIM Advisory branding (logo + company text).
   * Use Case 2: CLIENT_LOGO_SRC provided → show client logo alone.
   * Colors (toolbar/sidebar bg + text) are controlled by ThemeService via CSS variables.
   */
  protected initLogos(): void {
    const env = environment as any;
    const useAlt = env.USE_ALT_SIDENAV === true || env.USE_ALT_SIDENAV === 'true';
    this.useAltSidenav = useAlt;
    this.showThemeOptions = this.isEnabled(env.THEME_OPTIONS_ENABLED);
    this.showThemeToggle = this.isEnabled(env.THEME_TOGGLE_ENABLED);

    const clientSrc = (env.CLIENT_LOGO_SRC || '').trim();
    const clientIcon = (env.CLIENT_LOGO_ICON || '').trim();
    const clientText = (env.CLIENT_LOGO_TEXT || '').trim();
    this.clientLogo = clientSrc;
    this.clientIcon = clientIcon;
    this.clientText = clientText;
    this.hasClientLogo = !!clientSrc || !!clientIcon;

    if (useAlt) {
      this.logo = env.ALT_COMPANY_LOGO_SRC;
      this.secondaryLogo = env.ALT_SECONDARY_LOGO_SRC;
    } else {
      // Prefer DEFAULT_COMPANY_LOGO (local SVG) over COMPANY_LOGO_SRC (remote PNG) when set.
      const defaultLogo = (env.DEFAULT_COMPANY_LOGO || '').trim();
      this.logo = defaultLogo || environment.COMPANY_LOGO_SRC;
      // Prefer DEFAULT_MAKE_INDIA_LOGO (local SVG) over SECONDARY_LOGO_SRC (remote PNG) when set.
      const defaultMakeIndia = (env.DEFAULT_MAKE_INDIA_LOGO || '').trim();
      this.secondaryLogo = defaultMakeIndia || environment.SECONDARY_LOGO_SRC;
    }
  }

  private isEnabled(value: boolean | string | undefined): boolean {
    if (value === undefined || value === null || value === '') {
      return true;
    }
    return value === true || String(value).trim().toLowerCase() === 'true';
  }

  menuItems: any[] = [];
  expandedMenus: Set<string> = new Set();
  isChatRoute: boolean = false;
  // Macro panel state
  isMacroPanelOpen = false;
  isLoadingMacros = false;
  isLoadingMacroContent = false;
  macros: string[] = [];
  macroErrorMessage: string | null = null;

  constructor(
    protected http: HttpClient,
    private keycloakService: KeycloakService,
    protected router: Router,
    private responsiveLayoutService: ResponsiveLayoutService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private dashboardTitleService: DashboardTitleService,
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.isChatRoute = event.urlAfterRedirects.includes('/chat');
        if (this.isMobileLayout) {
          this.isMobileDrawerOpen = false;
        }
      });
  }


  async ngOnInit(): Promise<void> {
    this.breakpointObserver.observe(['(max-width: 991.98px)']).subscribe((state) => {
      this.isMobileLayout = state.matches;
      this.isMobileDrawerOpen = false;
    });

    this.initLogos();
    this.loadSidebarWidth();
    // Track active theme so the toolbar toggle icon reflects light/dark state.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      this.isDarkTheme = this.themeService.currentThemeName === 'dark';
    });
    // Show/hide the toolbar "Save Theme" button based on pending preview
    // state — true if EITHER the theme/custom OR the custom layout is in a
    // previewed-but-not-saved state. A single Save commits whichever is pending.
    this.pendingPreviewSub = combineLatest([
      this.themeService.pendingPreview$,
      this.layoutService.pendingLayout$,
    ]).subscribe(([themePending, layoutPending]) => {
      this.hasPendingThemePreview = themePending || layoutPending;
    });
    // Mirror the slideshow service state into local fields so the toolbar
    // template can show controls (play/pause/prev/next/apply/save/stop) and
    // the progress bar without juggling multiple async pipes — the service
    // is a singleton, so subscribing once here is enough.
    this.slideshowStateSub = this.slideshowService.state$.subscribe((s) => {
      this.slideshowActive = s.active;
      this.slideshowPlaying = s.playing;
      this.slideshowIndex = s.index;
      this.slideshowTotal = s.items.length;
      this.slideshowCurrentName = s.current?.name || '';
    });
    this.slideshowProgressSub = this.slideshowService.progress$.subscribe((p) => {
      this.slideshowProgress = p;
    });
    this.dashboardTitleSub = this.dashboardTitleService.state$.subscribe((state) => {
      this.dashboardTitle = state.title;
      this.dashboardTitlePosition = state.position;
      this.dashboardTitleFontSize = state.fontSize;
    });
    const isLoggedIn = await this.keycloakService.isLoggedIn();
    if (isLoggedIn) {
      const userDetails = await this.keycloakService.loadUserProfile();
      this.username =
        `${userDetails?.firstName} ${userDetails?.lastName}`.trim() || 'user';
    }
    this.loadSidenavConfig();
  }
  async logout(): Promise<void> {
    await this.keycloakService.logout(window.location.origin);
    localStorage.clear();
  }

  loadSidenavConfig() {
    this.http
      .get<any[]>('assets/side-navbar-config/sidenav.config.json')
      .subscribe((data) => {
        this.menuItems = data;
      });
  }

  toggleSidebar() {
    if (this.isMobileLayout) {
      this.isMobileDrawerOpen = !this.isMobileDrawerOpen;
      return;
    }
    this.isCollapsed = !this.isCollapsed;
    this.scheduleDesktopReflow();
  }

  /**
   * Heavy dashboards (like all-charts) may instantiate/defer widgets around the
   * sidebar transition window. Fire staged reflows so late-mounted widgets also
   * recalculate dimensions after expand/collapse.
   */
  protected scheduleDesktopReflow(): void {
    // Immediate pass
    this.responsiveLayoutService.triggerReflow();

    // During transition
    setTimeout(() => this.responsiveLayoutService.triggerReflow(), 180);

    // After transition end
    setTimeout(() => {
      this.responsiveLayoutService.triggerReflow();
      window.dispatchEvent(new Event('resize'));
    }, 360);
  }

  mouseenter() {
    if (!this.isExpanded) {
      this.isShowing = true;
    }
  }
  mouseleave() {
    if (!this.isExpanded) {
      this.isShowing = false;
    }
  }
  goToHelp() {
    const helpUrl = environment.HELP_URL;
    window.open(helpUrl, '_blank');
  }

  goToFeedback() {
    const feedbackUrl = environment.FEEDBACK_URL;
    if (feedbackUrl) window.open(feedbackUrl, '_blank');
  }

  showNotifications() {
    alert('Notifications clicked!');
  }

  openThemeSelector(): void {
    // Re-clicking the Theme Options button while the dialog is open used to
    // stack a second instance on top of the first. Guard against that by
    // tracking the live ref and bailing out if it's still open.
    if (this.themeSelectorDialogRef) {
      this.themeSelectorDialogRef.addPanelClass('dvm-theme-selector-pulse');
      return;
    }
    this.themeSelectorDialogRef = this.dialog.open(ThemeSelectorComponent, {
      panelClass: 'dvm-theme-selector-panel',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: false,
    });
    this.themeSelectorDialogRef.afterClosed().subscribe(() => {
      this.themeSelectorDialogRef = null;
    });
  }

  toggleSubmenu(menuName: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.expandedMenus.has(menuName)) {
      this.expandedMenus.delete(menuName);
    } else {
      this.expandedMenus.add(menuName);
    }
  }

  isMenuExpanded(menuName: string): boolean {
    return this.expandedMenus.has(menuName);
  }

  isActive(item: any): boolean {
    try {
      if (!item || !item.path) return false;
      return this.router.url.includes(item.path);
    } catch (e) {
      return false;
    }
  }

  canShowMacroShortcut(): boolean {
    return true;
  }

  toggleMacroPanel(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.isMacroPanelOpen = !this.isMacroPanelOpen;
  }

  selectMacro(macro: string): void {
    // Minimal selection handler — close panel and store selection
    this.isMacroPanelOpen = false;
    try {
      localStorage.setItem('selectedMacro', macro);
    } catch (e) {
      // ignore
    }
    console.log('Macro selected:', macro);
  }

  hasChildren(item: any): boolean {
    return item.children && item.children.length > 0;
  }

  /**
   * Menu items flagged "external" in sidenav.config.json navigate to an
   * env-specific URL (looked up via envUrlKey on the current `environment`)
   * instead of an internal route — e.g. Reports opens the standalone
   * spriced-reports app for the active qa/dev/uat/prod environment.
   */
  onMenuItemClick(item: any, event: Event): boolean {
    if (!item?.external || !item?.envUrlKey) return true;
    const url = (environment as any)[item.envUrlKey];
    if (!url) return true;
    event.preventDefault();
    window.location.href = url;
    return false;
  }
}
